import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Interface for structured error details
 */
export interface ErrorDetails {
  message?: string;
  details?: string;
  status?: number;
  statusText?: string;
  timestamp: Date;
}

/**
 * Service for centralized error handling and message building
 *
 * Provides consistent error message formatting across the application
 * and extracts useful information from HTTP errors.
 *
 * @example
 * constructor(private errorHandler: ErrorHandlerService) {}
 *
 * this.httpService.getData()
 *   .pipe(
 *     catchError(error => {
 *       const message = this.errorHandler.buildHttpErrorMessage(error, 'cargando datos');
 *       alert(message);
 *       return throwError(error);
 *     })
 *   )
 */
@Injectable()
export class ErrorHandlerService {

  constructor() { }

  /**
   * Builds a user-friendly error message from an HTTP error
   *
   * @param error - The error object (typically HttpErrorResponse)
   * @param context - Optional context description (e.g., "al crear registro en planeación")
   * @returns Formatted error message string ready to display to user
   *
   * @example
   * buildHttpErrorMessage(error, 'al crear registro en planeación')
   * // Returns:
   * // "Error al crear registro en planeación:
   * //
   * // Mensaje: Network error
   * // Status HTTP: 500
   * // Status Text: Internal Server Error"
   */
  buildHttpErrorMessage(error: any, context?: string): string {
    let errorMessage = context
      ? `Error ${context}:\n\n`
      : 'Error:\n\n';

    const details = this.extractErrorDetails(error);

    if (details.message) {
      errorMessage += `Mensaje: ${details.message}\n`;
    }

    if (details.details) {
      errorMessage += `Detalles: ${details.details}\n`;
    }

    if (details.status) {
      errorMessage += `Status HTTP: ${details.status}\n`;
    }

    if (details.statusText) {
      errorMessage += `Status Text: ${details.statusText}\n`;
    }

    // If no specific error info was found, provide generic message
    if (!details.message && !details.details && !details.status) {
      errorMessage += 'Error desconocido. Por favor revisa la consola para más detalles.';
    }

    return errorMessage;
  }

  /**
   * Extracts structured error details from various error types
   *
   * @param error - The error object
   * @returns ErrorDetails object with extracted information
   */
  extractErrorDetails(error: any): ErrorDetails {
    const details: ErrorDetails = {
      timestamp: new Date()
    };

    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      details.status = error.status;
      details.statusText = error.statusText;
      details.message = error.message;

      // Extract additional details from error body
      if (error.error) {
        if (typeof error.error === 'string') {
          details.details = error.error;
        } else if (error.error.message) {
          details.details = error.error.message;
        } else if (error.error.error) {
          details.details = error.error.error;
        }
      }
    }
    // Handle standard Error objects
    else if (error instanceof Error) {
      details.message = error.message;
    }
    // Handle plain objects
    else if (error && typeof error === 'object') {
      details.message = error.message || null;
      details.status = error.status || null;
      details.statusText = error.statusText || null;

      if (error.error) {
        if (typeof error.error === 'string') {
          details.details = error.error;
        } else if (error.error.message) {
          details.details = error.error.message;
        }
      }
    }

    return details;
  }

  /**
   * Logs error details to console in a structured format
   *
   * @param error - The error object
   * @param context - Optional context description
   */
  logError(error: any, context?: string): void {
    const details = this.extractErrorDetails(error);
    const prefix = context ? `[${context}]` : '[Error]';

    console.error(prefix, {
      timestamp: details.timestamp,
      message: details.message,
      details: details.details,
      status: details.status,
      statusText: details.statusText,
      originalError: error
    });
  }

  /**
   * Creates a simple error message for alerts
   * Shorter version without detailed breakdown
   *
   * @param error - The error object
   * @param context - Optional context description
   * @returns Short error message
   */
  buildSimpleErrorMessage(error: any, context?: string): string {
    const details = this.extractErrorDetails(error);
    const contextMsg = context ? ` ${context}` : '';

    if (details.details) {
      return `Error${contextMsg}: ${details.details}`;
    }

    if (details.message) {
      return `Error${contextMsg}: ${details.message}`;
    }

    if (details.status) {
      return `Error${contextMsg} (HTTP ${details.status})`;
    }

    return `Error${contextMsg}. Por favor contacta al administrador.`;
  }

  /**
   * Standard error messages for common operations
   * Use these for consistent user-facing error messages in Spanish
   */
  readonly MENSAJES = {
    // CRUD Operations
    ERROR_CREAR: 'Existió un error al crear el registro. Por favor intenta de nuevo.',
    ERROR_ACTUALIZAR: 'Existió un error al actualizar el registro. Por favor intenta de nuevo.',
    ERROR_ELIMINAR: 'Existió un error al eliminar el registro. Por favor intenta de nuevo.',
    ERROR_CARGAR: 'Existió un error al cargar la información. Por favor recarga la página.',

    // Specific operations
    ERROR_CANCELAR: 'Existió un error al cancelar. Por favor contacta al administrador del sistema.',
    ERROR_GUARDAR: 'Existió un error al guardar. Por favor intenta de nuevo.',
    ERROR_ENVIAR: 'Existió un error al enviar. Por favor intenta de nuevo.',
    ERROR_SUBIR_ARCHIVO: 'Existió un error al subir el archivo. Por favor intenta de nuevo.',

    // Connection errors
    ERROR_CONEXION: 'Error de conexión. Por favor verifica tu conexión a internet.',
    ERROR_SERVIDOR: 'Error del servidor. Por favor intenta más tarde.',
    ERROR_TIMEOUT: 'La operación tardó demasiado. Por favor intenta de nuevo.',

    // Validation errors
    ERROR_VALIDACION: 'Por favor revisa los datos ingresados.',
    ERROR_DATOS_INCOMPLETOS: 'Faltan datos requeridos. Por favor completa todos los campos.',
    ERROR_PERMISOS: 'No tienes permisos para realizar esta acción.',

    // Success messages
    EXITO_CREAR: 'El registro se ha creado con éxito.',
    EXITO_ACTUALIZAR: 'El registro se ha actualizado con éxito.',
    EXITO_ELIMINAR: 'El registro se ha eliminado con éxito.',
    EXITO_CANCELAR: 'La cancelación se ha realizado con éxito.',
    EXITO_GUARDAR: 'Los cambios se han guardado con éxito.',
    EXITO_ENVIAR: 'Se ha enviado con éxito.',

    // Generic
    ERROR_GENERICO: 'Existió un error. Por favor contacta al administrador del sistema.',
    CONTACTAR_ADMIN: 'Por favor contacta al administrador del sistema.'
  };

  /**
   * Builds a contextual error message for a specific operation
   *
   * @param operacion - The operation that failed (e.g., 'cancelar la confirmación')
   * @returns Formatted error message
   */
  buildOperationErrorMessage(operacion: string): string {
    return `Existió un error al ${operacion}. Por favor contacta al administrador del sistema.`;
  }

  /**
   * Builds a contextual success message for a specific operation
   *
   * @param operacion - The operation that succeeded (e.g., 'La confirmación')
   * @param accion - The action completed (e.g., 'cancelado')
   * @returns Formatted success message
   */
  buildOperationSuccessMessage(operacion: string, accion: string): string {
    return `${operacion} se ha ${accion} con éxito.`;
  }

  /**
   * Handles a Firebase/Firestore error and returns a user-friendly message
   *
   * @param error - The Firebase error object
   * @param context - Optional context description
   * @returns User-friendly error message
   */
  handleFirebaseError(error: any, context?: string): string {
    const contextMsg = context ? ` al ${context}` : '';

    // Common Firebase error codes
    if (error && error.code) {
      switch (error.code) {
        case 'permission-denied':
          return `Error${contextMsg}: No tienes permisos para realizar esta acción.`;
        case 'not-found':
          return `Error${contextMsg}: El registro no fue encontrado.`;
        case 'already-exists':
          return `Error${contextMsg}: El registro ya existe.`;
        case 'resource-exhausted':
          return `Error${contextMsg}: Se ha excedido el límite de operaciones. Intenta más tarde.`;
        case 'unavailable':
          return `Error${contextMsg}: El servicio no está disponible. Intenta más tarde.`;
        case 'deadline-exceeded':
          return `Error${contextMsg}: La operación tardó demasiado. Intenta de nuevo.`;
        default:
          return `Error${contextMsg}: ${error.message || this.MENSAJES.ERROR_GENERICO}`;
      }
    }

    return this.buildSimpleErrorMessage(error, context);
  }

  /**
   * Shows an error alert with consistent formatting
   *
   * @param message - The error message to display
   */
  showError(message: string): void {
    alert(message);
  }

  /**
   * Shows a success alert with consistent formatting
   *
   * @param message - The success message to display
   */
  showSuccess(message: string): void {
    alert(message);
  }

  /**
   * Shows a confirmation dialog and returns the result
   *
   * @param message - The confirmation message
   * @returns true if confirmed, false otherwise
   */
  confirmAction(message: string): boolean {
    return confirm(message);
  }

  /**
   * Standard confirmation messages for common destructive operations
   */
  readonly CONFIRMACIONES = {
    CANCELAR: '¿Estás seguro que deseas cancelar? Esta acción no se puede deshacer.',
    ELIMINAR: '¿Estás seguro que deseas eliminar este registro? Esta acción no se puede deshacer.',
    DESCARTAR_CAMBIOS: '¿Estás seguro que deseas descartar los cambios sin guardar?'
  };
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/error-handler.service.ts